import React, { useState, useEffect } from 'react'
import { withFormsy } from 'formsy-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Mark } from '@tiptap/core'
import { IconButton, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  inputWrapper: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
    padding: '14px',
    paddingRight: '100px',
    minHeight: '56px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)',
    },
    '&.focused': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      padding: '13px',
      paddingRight: '99px',
    },
    '&.error': {
      borderColor: theme.palette.error.main,
    },
  },
  editor: {
    flex: 1,
    '& .ProseMirror': {
      outline: 'none',
      fontSize: '1.4rem',
      lineHeight: '1',
    },
    '& .notranslate': {
      backgroundColor: 'rgb(232 255 254 / 1) ',
      padding: '2px 4px',
      borderRadius: '3px',
    },
  },
  translateButton: {
    position: 'absolute',
    right: '8px',
    padding: '4px',
  },
  counter: {
    position: 'absolute',
    right: '45px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1.4rem',
    color: theme.palette.text.secondary,
  },
  label: {
    position: 'absolute',
    top: '-8px',
    left: '12px',
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
    fontSize: '1rem',
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '1rem',
    marginTop: '4px',
  },
}))

const DoNotTranslate = Mark.create({
  name: 'doNotTranslate',

  parseHTML() {
    return [
      { tag: 'span[data-do-not-translate]' },
      { tag: 'span.do-not-translate' },
      {
        tag: 'span.notranslate',
        getAttrs: (node) => {
          return node.getAttribute('translate') === 'no' ? {} : false
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        class: 'notranslate',
        translate: 'no',
      },
      0,
    ]
  },

  addCommands() {
    return {
      toggleDoNotTranslate:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name)
        },
    }
  },
})

function TiptapTitleFormsy(props) {
  const {
    name,
    value,
    label,
    required,
    maxLength,
    setValue,
    errorMessage,
    isPristine,
    isValid,
  } = props

  const classes = useStyles()
  const [isFocused, setIsFocused] = useState(false)
  const [charCount, setCharCount] = useState(0)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        orderedList: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        bold: false,
        italic: false,
        code: false,
        strike: false,
        hardBreak: false,
      }),
      DoNotTranslate,
    ],
    content: value || '',
    editorProps: {
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          return true
        }

        const currentLength = view.state.doc.textContent.length
        const isCharacterKey =
          event.key.length === 1 && !event.ctrlKey && !event.metaKey

        if (currentLength >= maxLength && isCharacterKey) {
          const { from, to } = view.state.selection
          if (from === to) {
            event.preventDefault()
            return true
          }
        }

        return false
      },
      handlePaste: (view, event) => {
        event.preventDefault()

        const text = event.clipboardData?.getData('text/plain')
        if (text) {
          const singleLine = text.replace(/[\r\n]+/g, ' ')

          const currentText = view.state.doc.textContent
          const currentLength = currentText.length

          const availableSpace = maxLength - currentLength

          if (availableSpace > 0) {
            const textToPaste = singleLine.substring(0, availableSpace)
            view.dispatch(view.state.tr.insertText(textToPaste))
          }
        }
        return true
      },
    },
    onUpdate: ({ editor }) => {
      let plainText = editor.getText()
      const html = editor.getHTML()

      if (plainText.length > maxLength) {
        const { from } = editor.state.selection

        plainText = plainText.substring(0, maxLength)

        editor.commands.setContent(`<p>${plainText}</p>`)

        editor.commands.setTextSelection(Math.min(from, maxLength))
      }

      setCharCount(plainText.length)

      if (plainText.trim() === '') {
        setValue('')
      } else {
        setValue(html)
      }
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  })

  // Initialize from value
  useEffect(() => {
    if (value && editor && !editor.isDestroyed) {
      editor.commands.setContent(value)
      setCharCount(editor.getText().length)
    }
  }, [])

  const hasError = !isValid && !isPristine
  const isActive = editor?.isActive('doNotTranslate')

  const toggleDoNotTranslate = () => {
    editor?.chain().focus().toggleDoNotTranslate().run()
  }

  return (
    <div className={classes.container} name={name}>
      {label && (
        <Typography className={classes.label}>
          {label}
          {required && ' *'}
        </Typography>
      )}

      <div
        className={clsx(classes.inputWrapper, {
          focused: isFocused,
          error: hasError,
        })}
      >
        <div className={classes.editor}>
          <EditorContent editor={editor} />
        </div>

        {maxLength && (
          <Typography className={classes.counter}>
            {charCount}/{maxLength}
          </Typography>
        )}

        <Tooltip
          title={
            isActive
              ? 'Remove or Exit Do Not Translate'
              : 'Mark as Do Not Translate'
          }
        >
          <IconButton
            size="small"
            className={classes.translateButton}
            onClick={toggleDoNotTranslate}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              style={{ width: '20px', height: '20px' }}
            >
              <path
                fill={isActive ? '#00B2AD' : '#646464'}
                d="M4.123 5.189 3 6.312l2.905 2.905v6.009a1.325 1.325 0 0 0 1.321 1.321h5.283l.66 1.981h2.047l2.047 2.047 1.123-1.123-14.065-14-.198-.263Zm2.443 5.415a2.036 2.036 0 0 1 .066-.66l1.254 1.254a2.2 2.2 0 0 0 1.519 1.519l1.189 1.189a2.247 2.247 0 0 1-.66.066 3.381 3.381 0 0 1-3.368-3.368ZM11.122 11.132l.528.528c.075-.167.12-.346.132-.528h-.66Z"
              />
              <path
                fill={isActive ? '#00B2AD' : '#646464'}
                d="M19.112 5.981h-6.607L11.848 4H7.226a1.325 1.325 0 0 0-1.321 1.321v.594l1.981 2.047A3.2 3.2 0 0 1 9.805 7.3a3.26 3.26 0 0 1 2.245.858l-.858.858a1.76 1.76 0 0 0-1.325-.526 2.226 2.226 0 0 0-1.123.33l1.189 1.189h2.972c.058.215.08.438.066.66a3.378 3.378 0 0 1-.528 1.849l3.236 3.236-.66-1.915 2.047 2.047.594-.594-2.114-2.179a7.773 7.773 0 0 0 1.585-2.773h1.254v-.859h-2.971v-.859h-.859v.858h-.924l-.858-2.509h5.942a.662.662 0 0 1 .66.66v9.245a.662.662 0 0 1-.66.66h-1.188l.99.99h.594a1.325 1.325 0 0 0 1.321-1.321V7.3a1.325 1.325 0 0 0-1.32-1.319Zm-2.774 4.358a7.448 7.448 0 0 1-1.32 2.311 7.015 7.015 0 0 1-.594-.726l-.529-1.585h2.443Z"
              />
            </svg>
          </IconButton>
        </Tooltip>
      </div>

      {hasError && errorMessage && (
        <Typography className={classes.error}>{errorMessage}</Typography>
      )}
    </div>
  )
}

export default React.memo(withFormsy(TiptapTitleFormsy))
